/**
 * <oppie-delivery-countdown> — a live "order within Xu Ym, tomorrow at your door"
 * countdown shown above the cart checkout button.
 *
 * All time math runs in Europe/Amsterdam (the warehouse clock), not in the
 * browser's timezone. The element hides itself on weekends, on the holiday dates
 * from the theme setting and once today's cut-off has passed: we never promise a
 * delivery day we can't keep.
 *
 * Day word: same_day mode says "vanavond". Otherwise it is "morgen" when the next
 * working day is tomorrow, else that weekday's name. With a Friday cut-off
 * (data-friday-cutoff-*) Friday counts down to that time instead, for Saturday
 * delivery ("morgen"). We never deliver on Sunday or a holiday.
 */
const TIME_ZONE = 'Europe/Amsterdam';
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Wall-clock parts of `date` in the warehouse timezone.
 * @param {Date} date
 */
function warehouseClock(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value ?? '';
  const isoDate = `${get('year')}-${get('month')}-${get('day')}`;

  return {
    isoDate,
    // Weekday of the calendar date itself, independent of any timezone.
    weekday: new Date(`${isoDate}T12:00:00Z`).getUTCDay(),
    secondsOfDay: Number(get('hour')) * 3600 + Number(get('minute')) * 60 + Number(get('second')),
  };
}

/**
 * @param {string} isoDate - YYYY-MM-DD
 * @param {number} days
 */
function addDays(isoDate, days) {
  return new Date(Date.parse(`${isoDate}T12:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

class OppieDeliveryCountdown extends HTMLElement {
  #interval = null;

  connectedCallback() {
    this.valueEl = this.querySelector('[data-countdown-value]');
    this.dayEl = this.querySelector('[data-countdown-day]');
    this.cutoffSeconds = parseInt(this.dataset.cutoffHour, 10) * 3600 + parseInt(this.dataset.cutoffMinute, 10) * 60;
    this.fridayCutoffSeconds =
      parseInt(this.dataset.fridayCutoffHour, 10) * 3600 + parseInt(this.dataset.fridayCutoffMinute, 10) * 60;
    this.sameDay = this.dataset.mode === 'same_day';
    this.holidays = new Set((this.dataset.holidays || '').split(/[\s,]+/).filter(Boolean));
    this.unitH = this.dataset.unitH || 'u';
    this.unitM = this.dataset.unitM || 'm';
    this.unitS = this.dataset.unitS || 's';

    if (Number.isNaN(this.cutoffSeconds)) {
      this.hidden = true;
      return;
    }

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    this.#stop();
  }

  /** @param {string} isoDate */
  #isWorkingDay(isoDate) {
    const weekday = new Date(`${isoDate}T12:00:00Z`).getUTCDay();
    return weekday !== 0 && weekday !== 6 && !this.holidays.has(isoDate);
  }

  /**
   * Zaterdagbezorging: vrijdag (werkdag) met een vrijdag-deadline en een zaterdag
   * die geen feestdag is.
   * @param {{ isoDate: string, weekday: number }} clock
   */
  #isFridayRoute(clock) {
    return (
      !this.sameDay &&
      !Number.isNaN(this.fridayCutoffSeconds) &&
      clock.weekday === 5 &&
      this.#isWorkingDay(clock.isoDate) &&
      !this.holidays.has(addDays(clock.isoDate, 1))
    );
  }

  /**
   * @param {string} today - YYYY-MM-DD
   * @param {boolean} fridayRoute
   */
  #dayLabel(today, fridayRoute) {
    if (this.sameDay) return this.dataset.labelTonight || '';

    let next = addDays(today, 1);
    while (!fridayRoute && !this.#isWorkingDay(next)) next = addDays(next, 1);

    if (next === addDays(today, 1)) return this.dataset.labelTomorrow || '';

    try {
      return new Intl.DateTimeFormat(this.dataset.locale || document.documentElement.lang || 'nl', {
        weekday: 'long',
        timeZone: 'UTC',
      }).format(new Date(`${next}T12:00:00Z`));
    } catch {
      return '';
    }
  }

  #tick() {
    const clock = warehouseClock(new Date());
    const fridayRoute = this.#isFridayRoute(clock);
    const cutoff = fridayRoute ? this.fridayCutoffSeconds : this.cutoffSeconds;
    const remainingSeconds = cutoff - clock.secondsOfDay;

    if (!this.#isWorkingDay(clock.isoDate) || remainingSeconds <= 0) {
      this.hidden = true;
      this.#stop();
      return;
    }

    const dayLabel = this.#dayLabel(clock.isoDate, fridayRoute);
    if (!dayLabel) {
      this.hidden = true;
      this.#stop();
      return;
    }

    if (this.dayEl) this.dayEl.textContent = dayLabel;
    if (this.valueEl) this.valueEl.textContent = this.#format(remainingSeconds);
    this.hidden = false;
  }

  #stop() {
    if (this.#interval) clearInterval(this.#interval);
    this.#interval = null;
  }

  /** @param {number} totalSeconds */
  #format(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours >= 1) {
      return `${hours}${this.unitH} ${minutes}${this.unitM}`;
    }
    return `${minutes}${this.unitM} ${seconds}${this.unitS}`;
  }
}

if (!customElements.get('oppie-delivery-countdown')) {
  customElements.define('oppie-delivery-countdown', OppieDeliveryCountdown);
}
